import React, { useEffect, useState, useCallback } from "react";
import { api, resolveImg } from "@/lib/api";
import { uploadMedia, fmtSize, IMAGE_TYPES, VIDEO_TYPES } from "@/lib/upload";
import { toast } from "sonner";
import { Copy, Trash2, Film, ImageIcon, Upload, RotateCcw } from "lucide-react";

export function MediaDropzone({ accept, folder, label, onUploaded, compact = false, testId = "media-dropzone" }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [drag, setDrag] = useState(false);
  const inputRef = React.useRef(null);
  const allowed = accept === "image" ? IMAGE_TYPES : accept === "video" ? VIDEO_TYPES : [...IMAGE_TYPES, ...VIDEO_TYPES];

  const handle = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of list) {
      if (!allowed.includes(f.type)) { toast.error(`${f.name}: type not allowed`); continue; }
      const max = f.type.startsWith("video") ? 50 : 10;
      if (f.size > max * 1024 * 1024) { toast.error(`${f.name}: larger than ${max} MB`); continue; }
      try {
        setProgress(0);
        const doc = await uploadMedia(f, { folder, onProgress: setProgress });
        ok++;
        onUploaded?.(doc);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `${f.name}: upload failed`);
      }
    }
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    setBusy(false);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      onClick={() => !busy && inputRef.current?.click()}
      className={`rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${compact ? "px-3 py-3" : "px-6 py-8"} ${drag ? "border-gold-hi bg-gold/10" : "border-gold/40 hover:border-gold"} ${busy ? "opacity-70 pointer-events-none" : ""}`}
      data-testid={testId}
    >
      <input ref={inputRef} type="file" multiple={!compact} accept={allowed.join(",")} className="hidden" onChange={(e) => { handle(e.target.files); e.target.value = ""; }} data-testid={`${testId}-input`} />
      {busy ? (
        <div>
          <div className="text-gold-hi text-xs uppercase tracking-widest">Uploading… {progress}%</div>
          <div className="mt-2 h-1 rounded bg-card-warm overflow-hidden"><div className="h-full bg-gold transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-2 text-gold-hi">
          <Upload size={compact ? 14 : 18} />
          <span className={compact ? "text-xs" : "font-serif-lux text-lg"}>{label || "Drop files here or click"}</span>
        </div>
      )}
      {!compact && !busy && <div className="text-xs text-muted-warm mt-1">JPG/PNG/WEBP ≤10 MB · MP4/WEBM ≤50 MB · uploads in chunks</div>}
    </div>
  );
}

export function MediaLibrary() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("all");
  const load = useCallback(() => api.get("/admin/media").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const shown = kind === "all" ? items : items.filter((i) => i.kind === kind);

  const copy = (url) => { navigator.clipboard.writeText(resolveImg(url)); toast.success("URL copied"); };
  const remove = async (id) => {
    if (!window.confirm("Remove this file from the library? Anything using its URL will stop loading.")) return;
    await api.delete(`/admin/media/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="media-library">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif-lux text-xl text-gold-hi">Media Library</h2>
          <p className="text-xs text-muted-warm mt-1" data-testid="media-count">{items.length} file{items.length === 1 ? "" : "s"} · stored in Emergent Object Storage</p>
        </div>
        <div className="flex gap-1">
          {["all", "image", "video"].map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`media-filter-${k}`}
              className={`text-[11px] px-2.5 py-1 rounded capitalize ${kind === k ? "bg-gold text-obsidian" : "border border-gold/40 text-muted-warm hover:text-gold-hi"}`}>{k}</button>
          ))}
        </div>
      </div>
      <MediaDropzone accept="any" folder="media" onUploaded={load} testId="media-upload-dropzone" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {shown.map((f) => (
          <div key={f.id} className="glass-panel rounded-xl overflow-hidden" data-testid={`media-item-${f.id}`}>
            <div className="aspect-square bg-card-warm relative">
              {f.kind === "video" ? (
                <video src={resolveImg(f.url)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
              ) : (
                <img src={resolveImg(f.url)} alt={f.original_filename} className="w-full h-full object-cover" loading="lazy" />
              )}
              <span className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-widest bg-black/70 text-gold-hi px-1.5 py-0.5 rounded flex items-center gap-1">
                {f.kind === "video" ? <Film size={10} /> : <ImageIcon size={10} />}{f.kind}
              </span>
            </div>
            <div className="p-2">
              <div className="text-xs text-ivory truncate" title={f.original_filename}>{f.original_filename}</div>
              <div className="text-[10px] text-muted-warm">{fmtSize(f.size)}{f.width ? ` · ${f.width}×${f.height}` : ""} · {f.folder}</div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => copy(f.url)} className="flex-1 btn-outline-gold text-[10px] px-2 py-1 inline-flex items-center justify-center gap-1" data-testid={`media-copy-${f.id}`}><Copy size={10} /> Copy URL</button>
                <button onClick={() => remove(f.id)} className="text-red-300 hover:text-red-200 px-2" title="Remove" data-testid={`media-delete-${f.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="col-span-full text-sm text-muted-warm text-center py-8" data-testid="media-empty">No files yet. Drop images or videos above.</div>}
      </div>
    </div>
  );
}

export const SITE_IMAGE_SLOTS = [
  { key: "hero", label: "Hero background", hint: "Wide 16:9, dark, behind the headline", def: "/img/hero-lotus.png" },
  { key: "diyas", label: "Curiosity image", hint: "4:3, left of 'Not every image…'", def: "/img/temple-diyas.png" },
  { key: "yantra", label: "How-it-works banner", hint: "Very wide 3:1, text sits on the left", def: "/img/yantra-gold.png" },
  { key: "phone", label: "What-you-get image", hint: "Portrait/square, left of the ₹199 box", def: "/img/phone-wallpaper.png" },
  { key: "meditation", label: "7-Day image", hint: "Portrait, motto overlays the bottom", def: "/img/meditation-diya.png" },
  { key: "temple", label: "Tradition background", hint: "Wide, shown dimmed behind text", def: "/img/temple-marigold.png" },
];

export function SiteImages() {
  const [vals, setVals] = useState({});
  useEffect(() => { api.get("/settings/images").then((r) => setVals(r.data || {})); }, []);
  const set = async (key, url) => {
    const { data } = await api.patch("/admin/settings/images", { [key]: url });
    setVals(data);
    toast.success(url ? "Homepage image updated" : "Reset to default");
  };
  return (
    <div data-testid="site-images">
      <h2 className="font-serif-lux text-xl text-gold-hi">Homepage Images</h2>
      <p className="text-xs text-muted-warm mt-1 mb-6">Swap any section image without code. Changes are live immediately on all 13 language pages.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {SITE_IMAGE_SLOTS.map((s) => {
          const current = vals[s.key];
          return (
            <div key={s.key} className="glass-panel rounded-2xl overflow-hidden" data-testid={`site-image-${s.key}`}>
              <div className="aspect-video bg-card-warm relative">
                <img src={resolveImg(current || s.def)} alt={s.label} className="w-full h-full object-cover" data-testid={`site-image-img-${s.key}`} />
                {current && <span className="absolute top-2 left-2 text-[9px] uppercase tracking-widest bg-gold text-obsidian px-1.5 py-0.5 rounded" data-testid={`site-image-badge-${s.key}`}>Custom</span>}
              </div>
              <div className="p-3">
                <div className="text-sm text-ivory">{s.label}</div>
                <div className="text-[11px] text-muted-warm mb-3">{s.hint}</div>
                <MediaDropzone accept="image" folder="site" compact label="Replace image" onUploaded={(doc) => set(s.key, doc.url)} testId={`site-image-upload-${s.key}`} />
                {current && (
                  <button onClick={() => set(s.key, "")} className="mt-2 text-[11px] text-muted-warm hover:text-gold-hi inline-flex items-center gap-1" data-testid={`site-image-reset-${s.key}`}><RotateCcw size={11} /> Reset to default</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
